import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Loading from "../../components/Loading/Loading";
import { Package, ArrowLeft } from "lucide-react";

const ModifyInventory = () => {
  const { drug_id } = useParams();
  const { requestWithAuth } = useAuth();
  const [newDrugData, setNewDrugData] = useState({});
  const [drug, setDrug] = useState({});
  const [creatingDrug, setCreatingDrug] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setNewDrugData({ ...newDrugData, [e.target.name]: e.target.value });
  };

  const getDrug = async () => {
    setLoading(true);

    try {
      const response = await requestWithAuth(
        `/inventory/company/${drug_id}`,
        "GET"
      );
      setLoading(false);
      setDrug(response);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const updateDrug = async (e) => {
    e.preventDefault();
    setCreatingDrug(true);
    const body = JSON.stringify(newDrugData);
    try {
      const response = await requestWithAuth(
        `/inventory/company/${drug_id}/`,
        "PUT",
        body,
        {
          "Content-Type": "application/json",
        }
      );
      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Drug updated successfully",
      }).then(() => {
        window.location.href = "/administration/inventory";
      });
    } catch (error) {
      toast.error("Failed to update drug. Please try again later.");
    } finally {
      setCreatingDrug(false);
    }
  };

  useEffect(() => {
    getDrug();
  }, [drug_id]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Inventory</span>
          </button>

          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <span>Drugs</span>
            <span>/</span>
            <span>Inventory</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Modify</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Modify Drug</h1>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <form onSubmit={(e) => updateDrug(e)}>
              {/* Form Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Package className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Drug Information
                    </h2>
                    <p className="text-sm text-gray-500">
                      Update the drug details below
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Body */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Drug Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Drug Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={newDrugData?.name || drug?.name || ""}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter drug name"
                      required
                    />
                  </div>

                  {/* Reorder Level */}
                  <div>
                    <label
                      htmlFor="reorder_level"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Reorder Level <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="reorder_level"
                      name="reorder_level"
                      value={
                        newDrugData?.reorder_level || drug?.reorder_level || ""
                      }
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter reorder level"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Minimum quantity before reordering
                    </p>
                  </div>

                  {/* Cost Price */}
                  <div>
                    <label
                      htmlFor="cost_price"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Cost Price <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                        GH₵
                      </span>
                      <input
                        type="number"
                        id="cost_price"
                        name="cost_price"
                        step="0.01"
                        value={newDrugData?.cost_price || drug?.cost_price || ""}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>

                  {/* Cash Price */}
                  <div>
                    <label
                      htmlFor="cash_price"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Cash Price <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                        GH₵
                      </span>
                      <input
                        type="number"
                        id="cash_price"
                        name="cash_price"
                        step="0.01"
                        value={newDrugData?.cash_price || drug?.cash_price || ""}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Updating prices will notify all
                    dispensers of the changes.
                  </p>
                </div>
              </div>

              {/* Form Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingDrug}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingDrug ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating...
                    </>
                  ) : (
                    "Update Drug"
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModifyInventory;